import type { RouteComponentProps } from '@reach/router';
import { navigate } from 'gatsby';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import { LinkWrapper } from '@components/AddRecordButton/AddRecordButton.style';
import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import { ListItem, ListItemContainer } from '@components/ListItem';
import NullStateDiv from '@components/NullStateDiv';
import RoundedButton from '@components/RoundedButton';

import { ADD_INSURANCE, INSURANCE } from '@constants/routes';

import { useGetAllInsurance } from '@hooks/api/insurance';
import { useCuration } from '@hooks/useCuration';

const messages = defineMessages({
  title: {
    id: 'insurancePage.title',
    defaultMessage: 'Insurance',
  },
  noPrograms: {
    id: 'insurancePage.noPrograms',
    defaultMessage: 'No insurance programs found',
  },
  addRecord: {
    id: 'insurancePage.addRecord',
    defaultMessage: 'Add an insurance program',
  },
  addAnotherRecord: {
    id: 'insurancePage.addAnotherRecord',
    defaultMessage: 'Add another insurance program',
  },
});

const Insurance: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const { data, loading } = useGetAllInsurance();

  const { startAddingRecord } = useCuration();

  return (
    <ContentLoader loading={loading}>
      <ListItemContainer>
        {data.map((i) => (
          <ListItem
            description={i.planNumber}
            key={i.id}
            onClick={() => {
              navigate(`${INSURANCE}${i.id}`);
            }}
            title={i.provider ?? ''}
          />
        ))}

        {data.length === 0 && (
          <NullStateDiv>{formatMessage(messages.noPrograms)}</NullStateDiv>
        )}

        <LinkWrapper>
          <RoundedButton
            autoSize
            iconName="plus"
            onClick={() => {
              navigate(ADD_INSURANCE);
              startAddingRecord();
            }}
          >
            {data.length === 0
              ? formatMessage(messages.addRecord)
              : formatMessage(messages.addAnotherRecord)}
          </RoundedButton>
        </LinkWrapper>
      </ListItemContainer>
    </ContentLoader>
  );
};

export default withErrorBoundary(Insurance, {
  FallbackComponent: ErrorBoundaryFallback,
});
